import { useParams, useLocation, useNavigate } from "react-router-dom"
import { observer } from "mobx-react-lite"

import { useScaleApplication } from "@/domains/application/use-scale-application"
import { LockNftSwitchedEvent } from "@/domains/lock/events"
import { NftBreadcrumbWidget } from "@/domains/lock-page/components/nft-breadcrumb-widget"

import { ComplexBreadcrumbItem } from "../../types/complex-breadcrumb-item"
import { ComplexBreadcrumbDropdown, ComplexTab } from "../complex-breadcrumb-dropdown"

export const LockHomeComplexBreadcrumbApplication: ComplexBreadcrumbItem = {
  type: "complex",
  application: "lock-home",
  render: () => <LockHomeComplexBreadcrumbApplicationComponent />,
}

/**
 * Complex breadcrumb application for lock home pages
 * Shows current lock ID and allows navigation to other locks while preserving the current page
 */
export const LockHomeComplexBreadcrumbApplicationComponent = observer(() => {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const scaleApp = useScaleApplication()

  const locks = scaleApp.Lock.Repository.getAll()
  const currentLockId = id

  const tabs: Array<ComplexTab> = locks.map((lock) => ({
    id: String(lock.id),
    label: <NftBreadcrumbWidget nft={lock} />,
    tabLabel: `#${lock.id}`,
    slug: `/lock/${lock.id}/increase-amount`,
    description: `Lock #${lock.id}`,
    // icon: LockIcon,
  }))

  // Find current tab by matching lock ID from URL
  const currentTab = tabs.find((tab) => tab.id === currentLockId) ?? tabs[0]

  // Custom handler to preserve current page when changing locks
  const handleTabSelect = (tab: ComplexTab) => {
    const newNftId = Number(tab.id)
    const oldNftId = currentLockId ? Number(currentLockId) : null

    // Publish event when NFT is switched (only if it's actually different)
    if (oldNftId !== null && newNftId !== oldNftId) {
      scaleApp.EventBus.publish(new LockNftSwitchedEvent(oldNftId, newNftId))
    }

    // Extract current path segments: /lock/:id/:page
    const pathSegments = location.pathname.split("/")
    if (pathSegments.length >= 3 && pathSegments[1] === "lock") {
      // Replace the lock ID (index 2) while preserving the page (index 3+)
      pathSegments[2] = tab.id
      const newPath = pathSegments.join("/") + location.search + location.hash
      navigate(newPath)
    } else {
      // Fallback to default slug
      navigate(tab.slug)
    }
  }

  return (
    <ComplexBreadcrumbDropdown
      tabs={tabs}
      navigationMode="path"
      invalidTabMessage="Invalid lock"
      currentTabOverride={currentTab}
      onTabSelectOverride={handleTabSelect}
    />
  )
})
